import { useEffect } from 'react'
import { Pencil, Plus, Users } from 'lucide-react'
import { Link, useNavigate } from 'react-router'

import useCurrentUser from 'query/useCurrentUser'
import useUnassignedDesktopDevices from 'query/useUnassignedDesktopDevices'
import useUsers from 'query/useUsers'
import { Avatar, Badge, ButtonLink, DataTable, EmptyState, Figures, Icon, IconButtonLink, LoadingIndicator, PageContent, PageHeader } from 'ui'
import { formatRelativeTime } from 'utils/dates'

import DeleteUserConfirmModal from './DeleteUserConfirmModal'

const initialsForEmail = email => (email || '').split(/[@._-]/).slice(0, 2).map(part => part[0]).join('').toUpperCase()

export const UsersIndex = () => {
  const navigate = useNavigate()
  const { data: currentUser = {} } = useCurrentUser()
  const { data: users, isError, isLoading: isUsersLoading, invalidateQuery: invalidateUsersQuery } = useUsers()
  const { data: devices, isLoading: isDevicesLoading, invalidateQuery: invalidateDevicesQuery } = useUnassignedDesktopDevices()
  const isLoading = isUsersLoading || isDevicesLoading

  useEffect(() => {
    if (isError) navigate('/users/sign_in')
  }, [navigate, isError])

  const handleRemoveSuccess = () => {
    invalidateUsersQuery()
    invalidateDevicesQuery()
  }

  const rows = [
    ...users.map(user => ({ ...user, rowType: 'user' })),
    ...devices.map(device => ({ ...device, id: `device-${device.id}`, rowType: 'device' })),
  ]

  const columns = [
    {
      key: 'email',
      label: 'User',
      primary: true,
      render: row => row.rowType === 'user' ? (
        <span className="tw:flex tw:items-center tw:gap-3">
          <Avatar
            label={initialsForEmail(row.email)}
            size="sm"
            tone={(currentUser.id && row.id === currentUser.id) || (currentUser.email && row.email === currentUser.email) ? 'you' : 'member'}
          />
          <span className="tw:font-semibold tw:text-foreground-strong">{row.email}</span>
        </span>
      ) : (
        <span className="tw:flex tw:items-center tw:gap-3">
          <Avatar label="?" muted size="sm" />
          <span className="tw:grid tw:gap-1">
            <span className="tw:font-semibold tw:text-foreground-muted">Unnamed — {row.name}</span>
            <span className="ds-helper">Not yet activated</span>
          </span>
        </span>
      ),
    },
    {
      key: 'type',
      label: 'Role',
      render: row => row.rowType === 'user'
        ? <Badge>{row.type === 'admin' ? 'Admin' : 'Regular'}</Badge>
        : <span className="tw:text-foreground-subtle">—</span>,
    },
    {
      key: 'configuration',
      label: 'Configuration',
      render: row => row.configuration
        ? <Link to={`/configurations/${row.configuration.id}`}>{row.configuration.name}</Link>
        : '—',
    },
    {
      key: 'devices',
      label: 'Devices',
      render: row => <span className="tw:font-semibold tw:tabular-nums">{row.rowType === 'user' ? row.devices.length : 1}</span>,
    },
    {
      key: 'lastSeen',
      label: 'Last Seen',
      render: row => {
        if (row.rowType === 'device') return formatRelativeTime(row.lastSeen)
        const latest = [...row.devices].sort((a, b) => new Date(b.lastSeen) - new Date(a.lastSeen))[0]
        return latest ? formatRelativeTime(latest.lastSeen) : '—'
      },
    },
  ]

  return (
    <>
      <PageHeader
        action={rows.length > 0 ? <ButtonLink icon={<Icon as={Plus} />} to="/users/new">Add User</ButtonLink> : undefined}
        helpHref="https://docs.expandrive.com/web-management-console#users"
        helpLabel="Help — Users"
        subtitle="Manage team members, roles, and their assigned configurations."
        title="Active Users"
      />
      <PageContent>
        {isLoading ? <LoadingIndicator /> : (
          <>
            <Figures
              className="tw:mb-10"
              items={[
                { value: users.length + devices.length, label: 'Total Users' },
                { value: users.length, label: 'Named Users' },
                { value: devices.length, label: 'Unnamed Users' },
              ]}
            />
            <DataTable
              actions={row => row.rowType === 'user' && (
                <>
                  <IconButtonLink label={`Edit ${row.email}`} title="Edit User" to={`/users/${row.id}`}>
                    <Icon as={Pencil} />
                  </IconButtonLink>
                  <DeleteUserConfirmModal id={row.id} name={row.email} onDeleteSuccess={handleRemoveSuccess} />
                </>
              )}
              columns={columns}
              empty={(
                <EmptyState
                  action={<ButtonLink icon={<Icon as={Plus} />} to="/users/new">Add User</ButtonLink>}
                  icon={<Icon as={Users} size={32} />}
                  title="No Active Users"
                >
                  Add a user or have someone sign in to ExpanDrive to see them here.
                </EmptyState>
              )}
              rows={rows}
            />
          </>
        )}
      </PageContent>
    </>
  )
}

export default UsersIndex
